import { useState, useRef } from 'react';
import { X, Upload, FileSpreadsheet, Loader2, CheckCircle2, AlertTriangle, Download } from 'lucide-react';
import api from '@/lib/api';
import { toast } from '@/components/ui/toast';
import { extractErrorMessage } from '@/lib/errorMessage';

interface Props {
  onClose: () => void;
  onImported: () => void;
  onPlanLimit?: (msg: string) => void;
}

type Step = 'upload' | 'preview' | 'importing' | 'done';

const SAMPLE_CSV = `Name,First Name,Last Name,Phone,Email,Status,Source,Country,State,City,Priority,Campaign,Notes
Rahul Sharma,Rahul,Sharma,+919876543210,rahul@gmail.com,New,Facebook,India,Maharashtra,Mumbai,high,Summer Sale,Interested in demo
Priya Verma,Priya,Verma,+918765432109,priya@outlook.com,New,Google,India,Delhi,Delhi,medium,,Called once
Amit Patel,Amit,Patel,+917654321098;+917654321099,amit@yahoo.com;amit@work.com,New,Website,India,Gujarat,Pune,low,Contact Form,From website`;

function downloadSampleCsv() {
  const blob = new Blob([SAMPLE_CSV], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'leads-sample.csv';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export function CsvImportModal({ onClose, onImported, onPlanLimit }: Props) {
  const [step, setStep] = useState<Step>('upload');
  const [file, setFile] = useState<File | null>(null);
  const [headers, setHeaders] = useState<string[]>([]);
  const [previewRows, setPreviewRows] = useState<string[][]>([]);
  const [totalRows, setTotalRows] = useState(0);
  const [result, setResult] = useState<{ imported: number; merged?: number; failed: number; errors: { row: number; message: string }[] } | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const parseCsv = (text: string) => {
    const lines = text.split(/\r?\n/).filter(l => l.trim());
    if (lines.length < 2) { toast('error', 'CSV file is empty'); return; }

    const parseLine = (line: string) => {
      const result: string[] = [];
      let current = '', inQuotes = false;
      for (const ch of line) {
        if (ch === '"') inQuotes = !inQuotes;
        else if (ch === ',' && !inQuotes) { result.push(current.trim()); current = ''; }
        else current += ch;
      }
      result.push(current.trim());
      return result;
    };

    const hdrs = parseLine(lines[0]);
    const rows = lines.slice(1, 6).map(parseLine);
    setHeaders(hdrs);
    setPreviewRows(rows);
    setTotalRows(lines.length - 1);
    setStep('preview');
  };

  const handleFile = (f: File) => {
    if (!f.name.endsWith('.csv')) { toast('error', 'Please select a CSV file'); return; }
    setFile(f);
    const reader = new FileReader();
    reader.onload = (e) => parseCsv(e.target?.result as string);
    reader.readAsText(f);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const f = e.dataTransfer.files[0];
    if (f) handleFile(f);
  };

  const handleImport = async () => {
    if (!file) return;
    setStep('importing');
    const formData = new FormData();
    formData.append('file', file);
    try {
      const { data } = await api.post('/leads/import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000,
      });
      setResult(data);
      setStep('done');
      // Refresh the list when anything changed — imported OR merged (merged
      // updates the master row's fields, so the visible list also changes).
      if (data.imported > 0 || (data.merged ?? 0) > 0) onImported();
    } catch (err: any) {
      const msg = extractErrorMessage(err, 'Import failed');
      if (err?.response?.status === 403 && msg.toLowerCase().includes('upgrade')) {
        onPlanLimit?.(msg);
        onClose();
        return;
      }
      toast('error', msg);
      setStep('preview');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative z-10 w-full max-w-lg mx-4 bg-white rounded-2xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <FileSpreadsheet className="h-5 w-5 text-[#6366F1]" />
            <h2 className="text-lg font-semibold text-slate-900">Import Leads from CSV</h2>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="h-5 w-5" /></button>
        </div>

        <div className="px-6 py-5">
          {/* Step 1: Upload */}
          {step === 'upload' && (
            <>
              <div
                onDrop={handleDrop}
                onDragOver={e => e.preventDefault()}
                className="border-2 border-dashed border-slate-200 rounded-xl p-10 text-center hover:border-[#6366F1] hover:bg-indigo-50/30 transition-colors cursor-pointer"
                onClick={() => fileRef.current?.click()}
              >
                <Upload className="h-10 w-10 text-slate-300 mx-auto mb-3" />
                <p className="text-sm font-medium text-slate-700">Drop your CSV file here or click to browse</p>
                <p className="text-xs text-slate-400 mt-1">Max 5MB. Columns: Name, Phone, Email, Status, Source, Country, etc.</p>
                <input ref={fileRef} type="file" accept=".csv" className="hidden" onChange={e => { if (e.target.files?.[0]) handleFile(e.target.files[0]); }} />
              </div>
              <button
                onClick={downloadSampleCsv}
                className="flex items-center gap-1.5 text-xs font-medium text-[#6366F1] hover:text-[#4F46E5] mt-3 mx-auto"
              >
                <Download className="h-3.5 w-3.5" /> Download Sample CSV
              </button>
            </>
          )}

          {/* Step 2: Preview */}
          {step === 'preview' && (
            <div>
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm text-slate-600">
                  <span className="font-semibold">{file?.name}</span> — {totalRows} rows detected
                </p>
                <button onClick={() => { setStep('upload'); setFile(null); }} className="text-xs text-slate-400 hover:text-slate-600">Change file</button>
              </div>
              <div className="border border-slate-200 rounded-lg overflow-x-auto max-h-[250px]">
                <table className="text-xs w-full">
                  <thead className="bg-slate-100 border-b-2 border-slate-200">
                    <tr>
                      {headers.map((h, i) => (
                        <th key={i} className="px-3 py-2 text-left text-[10px] font-bold text-slate-700 uppercase tracking-wider whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {previewRows.map((row, ri) => (
                      <tr key={ri} className="border-t border-slate-100">
                        {row.map((cell, ci) => (
                          <td key={ci} className="px-3 py-1.5 text-slate-700 whitespace-nowrap max-w-[150px] truncate">{cell}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {previewRows.length < totalRows && (
                <p className="text-[11px] text-slate-400 mt-2">Showing first {previewRows.length} of {totalRows} rows</p>
              )}
            </div>
          )}

          {/* Step 3: Importing */}
          {step === 'importing' && (
            <div className="py-10 text-center">
              <Loader2 className="h-8 w-8 animate-spin text-[#6366F1] mx-auto mb-3" />
              <p className="text-sm font-medium text-slate-700">Importing {totalRows} leads...</p>
              <p className="text-xs text-slate-400 mt-1">This may take a moment</p>
            </div>
          )}

          {/* Step 4: Done */}
          {step === 'done' && result && (
            <div className="py-6 text-center">
              <CheckCircle2 className="h-10 w-10 text-emerald-500 mx-auto mb-3" />
              <p className="text-lg font-bold text-slate-900">Import complete</p>

              {/* Three colored stat tiles — imported / merged / failed.
                  `merged` only shows when backend reports it (auto-merge on). */}
              <div className="mt-4 flex justify-center gap-2">
                <div className="px-4 py-2 rounded-lg bg-emerald-50 border border-emerald-100">
                  <div className="text-xl font-bold text-emerald-600">{result.imported}</div>
                  <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-medium">Imported</div>
                </div>
                {(result.merged ?? 0) > 0 && (
                  <div className="px-4 py-2 rounded-lg bg-indigo-50 border border-indigo-100">
                    <div className="text-xl font-bold text-indigo-600">{result.merged}</div>
                    <div className="text-[10px] uppercase tracking-wider text-indigo-700 font-medium">Merged</div>
                  </div>
                )}
                {result.failed > 0 && (
                  <div className="px-4 py-2 rounded-lg bg-red-50 border border-red-100">
                    <div className="text-xl font-bold text-red-600">{result.failed}</div>
                    <div className="text-[10px] uppercase tracking-wider text-red-700 font-medium">Failed</div>
                  </div>
                )}
              </div>

              {(result.merged ?? 0) > 0 && (
                <p className="text-xs text-slate-500 mt-3">
                  {result.merged} duplicate{result.merged === 1 ? '' : 's'} matched existing leads and were merged.
                </p>
              )}

              {result.errors.length > 0 && (
                <div className="mt-4 text-left max-h-[150px] overflow-y-auto bg-slate-50 rounded-lg p-3">
                  {result.errors.map((e, i) => (
                    <div key={i} className="flex items-start gap-2 text-xs text-red-600 mb-1">
                      <AlertTriangle className="h-3 w-3 mt-0.5 flex-shrink-0" />
                      <span>Row {e.row}: {e.message}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-100">
          {step === 'done' ? (
            <button onClick={onClose} className="px-4 py-2 bg-[#6366F1] hover:bg-[#4F46E5] text-white text-sm font-semibold rounded-lg">
              Done
            </button>
          ) : step === 'preview' ? (
            <>
              <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 border border-gray-200 rounded-lg hover:bg-gray-50">Cancel</button>
              <button onClick={handleImport} className="px-4 py-2 bg-[#6366F1] hover:bg-[#4F46E5] text-white text-sm font-semibold rounded-lg flex items-center gap-2">
                <Upload className="h-3.5 w-3.5" /> Import {totalRows} Leads
              </button>
            </>
          ) : step === 'upload' ? (
            <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 border border-gray-200 rounded-lg hover:bg-gray-50">Cancel</button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
